import { Field } from 'formik';
import { useEffect, useRef, useState } from 'react';

export interface InputCodeProps {
  name: string;
  id: string;
  numberOfDigits: number;
  formik?: any;
  label?: string;
  errorMessage?: string;
  assistiveText?: string;
}

export const InputCode = ({
  assistiveText,
  id,
  errorMessage,
  formik,
  label,
  name,
  numberOfDigits,
}: InputCodeProps) => {
  const inputRefs = useRef<any>([]);
  const [digitArray, setDigitArray] = useState(
    [...Array(numberOfDigits)].map(() => '')
  );
  const [isInputValid, setIsInputValid] = useState(false);
  const [isInputValidated, setIsInputValidated] = useState(false);

  useEffect(() => {
    if (!digitArray.some((value) => isNaN(parseInt(value)))) {
      setIsInputValid(true);
      formik.setFieldValue(name, digitArray.join(''));
    } else {
      setIsInputValid(false);
      formik.setFieldValue(name, '');
    }
  }, [digitArray]);

  const addToInputRefs = (inputElement) => {
    if (inputElement && !inputRefs.current.includes(inputElement)) {
      inputRefs.current.push(inputElement);
    }
  };

  const handleInputChange = (ev, i) => {
    const value = ev.target.value;

    if (!isInputValidated) {
      setIsInputValidated(true);
    }

    const updatedDigitArray = [...digitArray];
    updatedDigitArray[i] = value;
    setDigitArray(updatedDigitArray);

    if (i < numberOfDigits - 1 && value !== '') {
      inputRefs.current[i + 1].select();
    }
  };

  const handleKeyDown = (ev, i) => {
    if (ev.keyCode === 8) {
      ev.preventDefault();

      const updatedDigitArray = [...digitArray];
      updatedDigitArray[i] = '';
      setDigitArray(updatedDigitArray);

      if (i > 0) {
        inputRefs.current[i - 1].select();
      }
    }
  };

  const handlePaste = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();

    const { clipboardData }: any = ev || window;
    const pastedData = clipboardData.getData('Text').trim();

    if (pastedData.length === numberOfDigits) {
      const updatedDigitArray = [...digitArray];
      setIsInputValidated(true);

      setDigitArray(pastedData.split(''));

      updatedDigitArray.forEach((inputValue, i) => {
        inputRefs.current[i].value = pastedData[i];
      });
    }
  };

  const checkInputValidity = () => {
    const error: any = {};

    if (!isInputValid) {
      error.format = true;
    }

    return Object.keys(error).length !== 0 ? error : null;
  };

  return (
    <div className="inline-flex flex-col w-64">
      <div className="relative flex flex-col w-full">
        <label
          htmlFor={'0'}
          className="absolute -top-4 text-xs font-medium text-black dark:text-white"
        >
          {label}
        </label>
        <div className="flex w-full justify-between">
          {digitArray.map((inputValue, i) => (
            <input
              type="text"
              autoComplete="off"
              maxLength={1}
              id={'dig-' + i}
              name={'dig-' + i}
              key={'dig-' + i}
              value={inputValue}
              className={`text-center pb-2 mt-1.5 w-8 border-solid rounded-none border-b dark:bg-grey-90 ${
                isInputValidated
                  ? isInputValid
                    ? 'border-positive'
                    : 'border-negative'
                  : 'border-black dark:border-white'
              }`}
              onChange={(ev) => handleInputChange(ev, i)}
              onKeyDown={(ev) => handleKeyDown(ev, i)}
              onPaste={(ev) => handlePaste(ev)}
              ref={addToInputRefs}
            />
          ))}
        </div>
        <Field name={name} validate={checkInputValidity}>
          {() => {
            return (
              <>
                <input
                  type="hidden"
                  id={id}
                  name={name}
                  value={formik.values[name]}
                  onChange={formik.handleChange}
                />
              </>
            );
          }}
        </Field>
        <div className="relative w-full">
          {errorMessage && (
            <span className="absolute left-0 top-1 text-negative text-xs">
              {errorMessage}
            </span>
          )}
        </div>
      </div>
      <small className="pt-2 block text-xs w-full text-left text-grey-50">
        {assistiveText}
      </small>
    </div>
  );
};
